/* Copyright (c) 2020-present CloudIO Inc */

import { toolTipProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './MenuItem';

const MenuItemManager: ComponentManagerFn<'MenuItem'> = (
  platform: Platform,
): ComponentManager<'MenuItem'> => {
  return {
    ...baseComponentManager<'MenuItem'>(platform, 'MenuItem'),
    type: 'MenuItem',
    name: 'Menu Item',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick', 'PulseTip'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      label: {
        label: 'Label',
        type: 'expression',
        returnType: async () => 'string',
      },
      icon: {
        label: 'Icon',
        type: 'iconselect',
        helpText: 'Icon to be placed before the text.',
      },
      ...toolTipProps,
    },
    getDisplayText: (item) => item.props.label.value,
    generateShortId: (item) =>
      item.label?.type === 'text' ? item.label.value : '',
    category: 'Toolbar',
    icon: { iconName: 'list-alt' },
    description: `**Overview**<br/>
The Menu Item is the fundamental actionable unit within a Menu or dropdown list. It represents a single choice that a user can select, typically rendering as a row with text and an optional icon.
Use this component to define the specific commands available in a context menu, such as "Edit," "Delete," or "View Details."

**1. Structure & Interactivity**<br/>
* **Parent Requirement:** Must be placed inside a Menu (or compatible container like UserProfileMenu).
* **Trigger Logic:** Supports a TriggerOnClick child component. This is where you define the action that executes when the item is selected (e.g., Navigate to Page, Run Script). It also supports PulseTip for onboarding or highlighting new menu options.

**2. Configuration**<br/>
* **Label:** The text displayed for the menu option. Supports expressions for dynamic labels (e.g., "Undo (Ctrl+Z)").
* **Icon:** An optional visual symbol displayed to the left of the label (e.g., a "Pencil" icon for Edit).
* **Tooltips:** Inherits standard tooltip properties (toolTipProps), allowing you to show extra context on hover.

**3. Best Use Cases**<br/>
* **Standard Actions:** "Copy," "Paste," "Cut."
* **Navigation:** "Go to Profile," "Settings."
* **State Toggles:** "Mark as Complete" (which might dynamically change to "Mark as Incomplete" using expressions).`,
  };
};

export default MenuItemManager;
